import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GoogleLogo, ArrowRight, Terminal, Clock, Plugs, ChatCircle } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';
import { useEffect } from 'react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    {
      icon: Plugs,
      title: 'Connect APIs',
      description: 'Configure and manage all your API integrations in one place',
    },
    {
      icon: Clock,
      title: 'Smart Scheduling',
      description: 'Advanced cron jobs with dependencies and retry logic',
    },
    {
      icon: Terminal,
      title: 'Real-time Logs',
      description: 'Monitor execution logs, errors, and performance metrics',
    },
    {
      icon: ChatCircle,
      title: 'Natural Language',
      description: 'Query and control APIs using plain English powered by AI',
    },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Login form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24 bg-kova-bg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md"
        >
          {/* Logo */}
          <div className="mb-12">
            <h1 className="font-heading font-black text-4xl tracking-tight">
              <span className="text-kova-cyan">KOVA</span>
            </h1>
            <p className="text-kova-slate mt-2 font-mono text-xs">
              // MERA API INTELLIGENCE HUB
            </p>
          </div>

          {/* Welcome text */}
          <h2 className="font-heading font-bold text-2xl mb-2">
            Welcome back
          </h2>
          <p className="text-kova-silv mb-8">
            Sign in to manage your API connections, schedules, and insights.
          </p>

          {/* Google Sign In */}
          <button
            data-testid="google-login-button"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white text-kova-bg font-medium py-3 px-4 hover:bg-[#f0f0f0] transition-colors"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </button>

          <p className="mt-6 text-kova-slate text-xs font-mono">
            By signing in, you agree to our Terms and Privacy Policy.
          </p>
        </motion.div>
      </div>

      {/* Right panel - Features showcase */}
      <div 
        className="hidden lg:flex flex-1 flex-col justify-center p-16 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #0C1220 0%, #060A12 100%)`,
        }}
      >
        {/* Background image */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1771793231904-a516cd3baff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3NzQzODcwOTV8MA&ixlib=rb-4.1.0&q=85)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Grid overlay */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(39,39,42,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(39,39,42,0.3) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <h3 className="font-heading font-bold text-3xl mb-4">
              Everything you need to
              <br />
              <span className="text-kova-cyan">orchestrate APIs</span>
            </h3>
            <p className="text-kova-silv mb-12 max-w-md">
              A unified dashboard for connecting, scheduling, monitoring, and querying all your API integrations.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                className="p-5 border border-kova-cyan/12 bg-kova-card/50 backdrop-blur-sm hover:border-kova-cyan/25 transition-colors"
              >
                <feature.icon size={24} className="text-kova-cyan mb-3" weight="duotone" />
                <h4 className="font-heading font-bold mb-1">{feature.title}</h4>
                <p className="text-kova-slate text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="mt-12 flex items-center gap-2 text-kova-cyan font-mono text-sm"
          >
            <ArrowRight size={16} />
            <span>Start building in minutes</span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
